import React, { useMemo, useState } from "react";
import { Col } from "react-bootstrap";
import {
  Button,
  ButtonGroup,
  Chip,
  TextField,
  Tooltip,
  makeStyles,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import Skeleton from "@material-ui/lab/Skeleton";
import {
  CheckRounded,
  CloseRounded,
  Delete as DeleteIcon,
  MenuBook as MenuBookIcon,
  CloudUpload as CloudUploadIcon,
  Search as SearchIcon,
} from "@material-ui/icons";
import { MDBDataTable } from "mdbreact";
import swal from "sweetalert";
import { toast } from "react-hot-toast";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  agregarLibroFila,
  eliminarLibroFila,
  fetchLibrosFila,
  isEstaticoTrue,
} from "../../../../shared/fetchers/filasHome";
import CustomGreenButton from "../../../../components/shared/custom-green-button";
import ExcelWorkBook from "../../../../components/shared/ExcelWorkBook";
import ImportarLibrosMasivo from "./importar-libros-masivo";

const GestionarLibros = ({ fila, libros = [], onVolver }) => {
  const classes = useStyles();
  const queryClient = useQueryClient();
  const filaName = fila?.filaHomeName;

  const [libroSeleccionado, setLibroSeleccionado] = useState(null);
  const [vista, setVista] = useState("individual");

  const { data: librosFila = [], isLoading } = useQuery(
    ["librosFila", filaName],
    () => fetchLibrosFila(filaName),
    { enabled: Boolean(filaName) }
  );

  const idsEnFila = useMemo(
    () => new Set(librosFila.map((l) => String(l.idLibro))),
    [librosFila]
  );

  const librosDisponibles = useMemo(() => {
    const all = libros?.data ?? libros ?? [];
    return all.filter((l) => !idsEnFila.has(String(l.idLibro)));
  }, [libros, idsEnFila]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["librosFila", filaName] });
    queryClient.invalidateQueries({ queryKey: ["filasHome"] });
  };

  const handleAdd = async (e) => {
    e?.preventDefault?.();
    if (!libroSeleccionado?.idLibro) return;

    try {
      const res = await agregarLibroFila({
        idLibro: libroSeleccionado.idLibro,
        nombreFila: filaName,
      });
      if (!res?.status) throw new Error(res?.info ?? "Error");

      toast.success(
        `${libroSeleccionado.idLibro} - ${libroSeleccionado.title} agregado`
      );
      setLibroSeleccionado(null);
      invalidate();
    } catch (error) {
      toast.error(error?.message || "Error agregando el libro");
    }
  };

  const handleRemove = async (libro) => {
    const verify = await swal({
      title: "¿Eliminar libro de la fila?",
      text: `Se quitará "${libro.title ?? libro.idLibro}" de "${filaName}".`,
      icon: "warning",
      buttons: ["Cancelar", "Eliminar"],
      dangerMode: true,
    });
    if (!verify) return;

    try {
      const res = await eliminarLibroFila({
        idLibro: libro.idLibro,
        nombreFila: filaName,
      });
      if (!res?.status) throw new Error(res?.info ?? "Error");
      toast.success("Libro eliminado de la fila");
      invalidate();
    } catch (error) {
      toast.error(error?.message || "Error eliminando el libro");
    }
  };

  const tableRows = useMemo(
    () =>
      librosFila.map((row) => ({
        ...row,
        acciones: (
          <button
            className={classes.removeBtn}
            onClick={() => handleRemove(row)}
            title="Quitar de la fila"
          >
            <DeleteIcon style={{ fontSize: 14, marginRight: 4 }} />
            Quitar
          </button>
        ),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [librosFila]
  );

  const tableColumns = [
    { label: "ID", field: "idLibro", sort: "asc", width: 120 },
    { label: "Título", field: "title", sort: "asc" },
    { label: "Acciones", field: "acciones", sort: "disabled", width: 120 },
  ];

  return (
    <Col xs={12} className="content-white-box px-4 py-4">
      <div className="d-flex align-items-center justify-content-between flex-wrap">
        <div className="d-flex align-items-center">
          <MenuBookIcon style={{ fontSize: 22 }} />
          <div className="admin-main-title-dark-gray ml-2 mt-1">
            Libros de la fila:&nbsp;
            <span style={{ color: "#5fbeaa" }}>{filaName}</span>
          </div>
        </div>
        <Button
          variant="outlined"
          size="small"
          onClick={onVolver}
          startIcon={<CloseRounded />}
        >
          Volver a la lista
        </Button>
      </div>

      <div className="d-flex align-items-center mt-2 mb-3" style={{ gap: 8 }}>
        <Chip
          size="small"
          label={`${librosFila.length} libro${
            librosFila.length === 1 ? "" : "s"
          } asignado${librosFila.length === 1 ? "" : "s"}`}
        />
        {fila?.disponibilidadRegional && (
          <Chip
            size="small"
            variant="outlined"
            label={`Disponibilidad: ${fila.disponibilidadRegional}`}
          />
        )}
        {isEstaticoTrue(fila?.estatico) && (
          <Chip
            size="small"
            label="Posición fija"
            style={{ background: "#ffe8cc", color: "#b35900" }}
          />
        )}
      </div>

      <ButtonGroup
        size="small"
        variant="outlined"
        className="mb-3"
        aria-label="modos de gestión"
      >
        <Button
          onClick={() => setVista("individual")}
          variant={vista === "individual" ? "contained" : "outlined"}
          color={vista === "individual" ? "primary" : "default"}
          startIcon={<SearchIcon />}
        >
          Agregar individual
        </Button>
        <Button
          onClick={() => setVista("masiva")}
          variant={vista === "masiva" ? "contained" : "outlined"}
          color={vista === "masiva" ? "primary" : "default"}
          startIcon={<CloudUploadIcon />}
        >
          Importación masiva
        </Button>
      </ButtonGroup>

      {vista === "individual" ? (
        <form
          className={classes.addForm}
          onSubmit={handleAdd}
          autoComplete="off"
        >
          <Autocomplete
            options={librosDisponibles}
            getOptionLabel={(option) =>
              option?.idLibro ? `${option.idLibro} - ${option.title}` : ""
            }
            value={libroSeleccionado}
            onChange={(_, value) => setLibroSeleccionado(value)}
            noOptionsText="Sin resultados"
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                size="small"
                placeholder="Buscar libro por ID o título"
                fullWidth
              />
            )}
            style={{ flex: 1, minWidth: 280 }}
          />
          <Button
            type="submit"
            variant="contained"
            startIcon={<CheckRounded />}
            disabled={!libroSeleccionado?.idLibro}
            classes={{ root: classes.addBtn }}
          >
            Agregar a la fila
          </Button>
        </form>
      ) : (
        <ImportarLibrosMasivo
          filaHome={filaName}
          libros={libros?.data ?? libros}
          librosFila={librosFila}
        />
      )}

      <hr className="mt-4" />

      <div className="d-flex align-items-center justify-content-between flex-wrap mt-3 mb-2">
        <div>
          <div className="admin-main-title-dark-gray">Libros actuales</div>
          <div className="admin-muted-text-small">
            Listado completo de libros pertenecientes a esta fila.
          </div>
        </div>

        {librosFila.length > 0 && (
          <ButtonGroup size="small" variant="contained">
            <CustomGreenButton
              onClick={() => {
                navigator.clipboard
                  .writeText(JSON.stringify(librosFila))
                  .then(() =>
                    swal({
                      title: "Tabla copiada",
                      icon: "success",
                    })
                  );
              }}
            >
              Copy
            </CustomGreenButton>
            <CustomGreenButton>
              <ExcelWorkBook
                columns={tableColumns.slice(0, -1).map((c) => ({ ...c, isVisible: true }))}
                rows={librosFila}
                filename={`Libros-${filaName}`}
              />
            </CustomGreenButton>
          </ButtonGroup>
        )}
      </div>

      {isLoading ? (
        <>
          <Skeleton animation="wave" />
          <Skeleton animation="wave" />
          <Skeleton animation="wave" />
        </>
      ) : tableRows.length === 0 ? (
        <div className={classes.empty}>
          <Tooltip title="Aún no hay libros en esta fila">
            <MenuBookIcon style={{ fontSize: 32, color: "#bbb" }} />
          </Tooltip>
          <div className="mt-2 admin-muted-text-small">
            Esta fila aún no tiene libros. Usa el formulario de arriba para
            agregar uno o varios.
          </div>
        </div>
      ) : (
        <MDBDataTable
          small
          responsive
          striped
          bordered
          searchTop
          searchBottom={false}
          pagingTop
          data={{ columns: tableColumns, rows: tableRows }}
          noBottomColumns
          entriesOptions={[5, 10, 20, 50]}
          entriesLabel="Filas"
          searchLabel="Buscar"
          paginationLabel={["Anterior", "Siguiente"]}
          infoLabel={["Mostrando", "a", "de", "libros"]}
        />
      )}
    </Col>
  );
};

export default GestionarLibros;

const useStyles = makeStyles({
  addForm: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
    alignItems: "center",
  },
  addBtn: {
    background: "#81c868",
    color: "#fff",
    "&:hover": { backgroundColor: "#6fb558" },
    "&.Mui-disabled": { background: "#cdeac3", color: "#fff" },
  },
  removeBtn: {
    border: "none",
    padding: "3px 8px",
    backgroundColor: "#dc3545",
    fontWeight: 500,
    color: "#fff",
    borderRadius: 3,
    fontSize: "0.85em",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
  },
  empty: {
    textAlign: "center",
    padding: "30px 0",
  },
});
